import React, { useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { Theme, darkTheme, lightTheme } from '../theme';
import { Prediction } from '../types/predictions';
import { calculateFairLeaderboard, ScoringResult, PriceDataPoint } from '../utils/leaderboardScoring';
import axios from 'axios';

const PageContainer = styled.div`
  min-height: 100vh;
  background: ${props => props.theme.background};
  color: ${props => props.theme.text};
  padding: 40px 20px;
`;

const ContentWrapper = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 48px;
`;

const Title = styled.h1`
  font-size: 48px;
  font-weight: 800;
  margin: 0 0 16px 0;
  background: linear-gradient(135deg, #FFD700 0%, #FFA500 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  
  @media (max-width: 768px) {
    font-size: 32px;
  }
`;

const Subtitle = styled.p`
  font-size: 18px;
  color: ${props => props.theme.subText};
  margin: 0;
  max-width: 600px;
  margin: 0 auto;
  line-height: 1.6;
`;

const TwoColumn = styled.div`
  display: grid;
  grid-template-columns: 1fr 400px;
  gap: 40px;
  
  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const LeaderboardSection = styled.div`
  background: ${props => props.theme.surface};
  border-radius: 16px;
  padding: 24px;
  border: 1px solid ${props => props.theme.border};
`;

const SectionTitle = styled.h2`
  font-size: 20px;
  font-weight: 700;
  margin: 0 0 20px 0;
  color: ${props => props.theme.text};
  display: flex;
  align-items: center;
  gap: 10px;
`;

const LeaderRow = styled.div<{ rank: number }>`
  display: flex;
  align-items: center;
  padding: 16px;
  margin-bottom: 12px;
  background: ${props => {
    if (props.rank === 1) return 'linear-gradient(135deg, rgba(255, 215, 0, 0.15) 0%, rgba(255, 215, 0, 0.05) 100%)';
    if (props.rank === 2) return 'linear-gradient(135deg, rgba(192, 192, 192, 0.15) 0%, rgba(192, 192, 192, 0.05) 100%)';
    if (props.rank === 3) return 'linear-gradient(135deg, rgba(205, 127, 50, 0.15) 0%, rgba(205, 127, 50, 0.05) 100%)';
    return props.theme.background;
  }};
  border-radius: 12px;
  border-left: 4px solid ${props => {
    if (props.rank === 1) return '#FFD700';
    if (props.rank === 2) return '#C0C0C0';
    if (props.rank === 3) return '#CD7F32';
    return props.theme.border;
  }};
  transition: all 0.2s ease;
  
  &:hover {
    transform: translateX(6px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }
`;

const RankBadge = styled.div<{ rank: number }>`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  font-size: 16px;
  margin-right: 16px;
  flex-shrink: 0;
  background: ${props => {
    if (props.rank === 1) return 'linear-gradient(135deg, #FFD700, #FFA500)';
    if (props.rank === 2) return 'linear-gradient(135deg, #C0C0C0, #808080)';
    if (props.rank === 3) return 'linear-gradient(135deg, #CD7F32, #8B4513)';
    return props.theme.border;
  }};
  color: ${props => props.rank <= 3 ? '#fff' : props.theme.text};
  box-shadow: ${props => props.rank <= 3 ? '0 2px 8px rgba(0,0,0,0.2)' : 'none'};
`;

const ProfileImage = styled.img`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  object-fit: cover;
  margin-right: 16px;
  flex-shrink: 0;
  border: 2px solid ${props => props.theme.border};
`;

const ProfilePlaceholder = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: ${props => props.theme.background};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  margin-right: 16px;
  flex-shrink: 0;
  border: 2px solid ${props => props.theme.border};
`;

const LeaderInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const LeaderName = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: ${props => props.theme.text};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const LeaderStats = styled.div`
  font-size: 13px;
  color: ${props => props.theme.subText};
  margin-top: 4px;
  display: flex;
  gap: 12px;
`;

const ScoreSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  margin-left: 16px;
`;

const MainScore = styled.div<{ score: number }>`
  font-size: 24px;
  font-weight: bold;
  color: ${props => {
    if (props.score >= 70) return '#22C55E';
    if (props.score >= 50) return '#F59E0B';
    return '#EF4444';
  }};
`;

const ScoreLabel = styled.div`
  font-size: 11px;
  color: ${props => props.theme.subText};
  margin-top: 2px;
`;

const BonusTag = styled.span`
  font-size: 10px;
  background: rgba(34, 197, 94, 0.1);
  color: #22C55E;
  padding: 2px 6px;
  border-radius: 4px;
  margin-top: 4px;
`;

// Methodology Section
const MethodologySection = styled.div`
  background: ${props => props.theme.surface};
  border-radius: 16px;
  padding: 24px;
  border: 1px solid ${props => props.theme.border};
  height: fit-content;
  position: sticky;
  top: 20px;
`;

const MethodTitle = styled.h3`
  font-size: 16px;
  font-weight: 700;
  margin: 24px 0 12px 0;
  color: ${props => props.theme.text};
  display: flex;
  align-items: center;
  gap: 8px;
  
  &:first-of-type {
    margin-top: 0;
  }
`;

const MethodText = styled.p`
  font-size: 14px;
  color: ${props => props.theme.subText};
  margin: 0 0 12px 0;
  line-height: 1.6;
`;

const FormulaBox = styled.div`
  background: ${props => props.theme.background};
  border-radius: 8px;
  padding: 16px;
  margin: 12px 0;
  font-family: 'SF Mono', Monaco, monospace;
  font-size: 13px;
  color: ${props => props.theme.text};
  border: 1px solid ${props => props.theme.border};
  overflow-x: auto;
`;

const FactorList = styled.ul`
  margin: 0;
  padding-left: 20px;
  color: ${props => props.theme.subText};
  font-size: 14px;
  line-height: 1.8;
`;

const FactorItem = styled.li`
  margin-bottom: 8px;
  
  strong {
    color: ${props => props.theme.text};
  }
`;

const HighlightBox = styled.div`
  background: linear-gradient(135deg, rgba(255, 215, 0, 0.1) 0%, rgba(255, 165, 0, 0.05) 100%);
  border-left: 3px solid #FFD700;
  padding: 12px 16px;
  border-radius: 0 8px 8px 0;
  margin: 16px 0;
`;

const HighlightText = styled.p`
  font-size: 13px;
  color: ${props => props.theme.text};
  margin: 0;
  font-style: italic;
`;

const BackLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: ${props => props.theme.subText};
  text-decoration: none;
  font-size: 14px;
  margin-bottom: 24px;
  transition: color 0.2s;
  
  &:hover {
    color: ${props => props.theme.text};
  }
`;

const LoadingState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: ${props => props.theme.subText};
  font-size: 16px;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: ${props => props.theme.subText};
`;

interface LeaderboardPageProps {
  isDarkMode?: boolean;
  theme?: Theme;
}

const LeaderboardPage: React.FC<LeaderboardPageProps> = ({ 
  isDarkMode = true,
  theme = darkTheme 
}) => {
  const [predictions, setPredictions] = useState<Prediction[]>([]);
  const [chartData, setChartData] = useState<PriceDataPoint[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch predictions and price data in parallel
        const [predictionsRes, pricesRes] = await Promise.all([
          axios.get('/api/predictions'),
          axios.get('/api/prices?asset=BTC')
        ]);
        
        setPredictions(predictionsRes.data);
        setChartData(pricesRes.data.map((p: any) => ({
          time: p.time || p.timestamp,
          value: p.value || p.close || p.price
        })));
        
      } catch (err: any) {
        console.error('Failed to fetch leaderboard data:', err);
        setError(err.message || 'Failed to load leaderboard');
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, []);

  const leaderboard = useMemo(() => {
    if (!predictions.length || !chartData.length) return [];
    return calculateFairLeaderboard(predictions, chartData, 20);
  }, [predictions, chartData]);

  const getMedal = (rank: number): string => {
    if (rank === 1) return '🥇';
    if (rank === 2) return '🥈';
    if (rank === 3) return '🥉';
    return `${rank}`;
  };

  const currentTheme = isDarkMode ? darkTheme : lightTheme;

  return (
    <PageContainer theme={currentTheme}>
      <ContentWrapper>
        <BackLink href="/btc" theme={currentTheme}>
          ← Back to Chart
        </BackLink>
        
        <Header>
          <Title>🏆 Prediction Leaderboard</Title>
          <Subtitle theme={currentTheme}>
            Track the most accurate Bitcoin price predictors using our fair, 
            multi-factor scoring system that rewards both accuracy and consistency.
          </Subtitle>
        </Header>
        
        <TwoColumn>
          <LeaderboardSection theme={currentTheme}>
            <SectionTitle theme={currentTheme}>
              <span>📊</span> Current Rankings
            </SectionTitle>
            
            {loading ? (
              <LoadingState theme={currentTheme}>
                Loading leaderboard...
              </LoadingState>
            ) : error ? (
              <EmptyState theme={currentTheme}>
                {error}
              </EmptyState>
            ) : leaderboard.length === 0 ? (
              <EmptyState theme={currentTheme}>
                No resolved predictions yet. Check back soon!
              </EmptyState>
            ) : (
              leaderboard.map((entry, index) => {
                const rank = index + 1;
                const hasBonus = entry.proximityCredits > 0 || entry.directionalBonuses > 0;
                
                return (
                  <LeaderRow key={entry.name} rank={rank} theme={currentTheme}>
                    <RankBadge rank={rank} theme={currentTheme}>
                      {getMedal(rank)}
                    </RankBadge>
                    
                    {entry.profileImage ? (
                      <ProfileImage src={entry.profileImage} alt={entry.name} theme={currentTheme} />
                    ) : (
                      <ProfilePlaceholder theme={currentTheme}>
                        {entry.name.charAt(0).toUpperCase()}
                      </ProfilePlaceholder>
                    )}
                    
                    <LeaderInfo>
                      <LeaderName theme={currentTheme}>{entry.name}</LeaderName>
                      <LeaderStats theme={currentTheme}>
                        <span>✅ {entry.successes}</span>
                        <span>❌ {entry.misses}</span>
                        <span>⏳ {entry.pending}</span>
                        <span style={{ opacity: 0.7 }}>
                          ({entry.traditionalRate}% raw)
                        </span>
                      </LeaderStats>
                    </LeaderInfo>
                    
                    <ScoreSection>
                      <MainScore score={entry.displayScore} theme={currentTheme}>
                        {entry.displayScore}
                      </MainScore>
                      <ScoreLabel theme={currentTheme}>Fair Score</ScoreLabel>
                      {hasBonus && (
                        <BonusTag>
                          +{((entry.proximityCredits + entry.directionalBonuses) * 10).toFixed(0)} bonus
                        </BonusTag>
                      )}
                    </ScoreSection>
                  </LeaderRow>
                );
              })
            )}
          </LeaderboardSection>
          
          <MethodologySection theme={currentTheme}>
            <SectionTitle theme={currentTheme}>
              <span>📐</span> Scoring Methodology
            </SectionTitle>
            
            <MethodText theme={currentTheme}>
              Our leaderboard uses a sophisticated multi-factor scoring system 
              designed to be fair to both newcomers and experienced predictors.
            </MethodText>
            
            <HighlightBox>
              <HighlightText theme={currentTheme}>
                "A predictor with 8/10 (80%) accuracy should rank higher than 
                someone with 1/1 (100%) because sample size matters."
              </HighlightText>
            </HighlightBox>
            
            <MethodTitle theme={currentTheme}>
              🧮 The Formula
            </MethodTitle>
            <FormulaBox theme={currentTheme}>
              Fair Score = Raw Score × √(Wilson) × √(Volume) × LeadTime
            </FormulaBox>
            
            <MethodTitle theme={currentTheme}>
              📊 Scoring Factors
            </MethodTitle>
            <FactorList theme={currentTheme}>
              <FactorItem theme={currentTheme}>
                <strong>Wilson Score</strong> — Statistical confidence interval 
                that accounts for sample size. Used by Reddit, Yelp, and Amazon.
              </FactorItem>
              <FactorItem theme={currentTheme}>
                <strong>Proximity Score</strong> — Partial credit for close misses:
                within 5% = 90% credit, within 10% = 75% credit, etc.
              </FactorItem>
              <FactorItem theme={currentTheme}>
                <strong>Directional Bonus</strong> — +25% bonus for correctly 
                predicting if price goes up or down, even if target was off.
              </FactorItem>
              <FactorItem theme={currentTheme}>
                <strong>Volume Factor</strong> — Rewards consistent participation. 
                10+ predictions = full credit. √(n/10) formula.
              </FactorItem>
              <FactorItem theme={currentTheme}>
                <strong>Lead Time Bonus</strong> — Longer-term predictions are harder. 
                1+ year ahead: +30%, 6mo: +20%, 3mo: +10%.
              </FactorItem>
            </FactorList>
            
            <MethodTitle theme={currentTheme}>
              ⚖️ Why This System?
            </MethodTitle>
            <MethodText theme={currentTheme}>
              Simple success rate (wins/total) unfairly ranks one-hit wonders 
              above consistent predictors. Our system rewards:
            </MethodText>
            <FactorList theme={currentTheme}>
              <FactorItem theme={currentTheme}>
                <strong>Consistency</strong> — Make more predictions to prove your skill
              </FactorItem>
              <FactorItem theme={currentTheme}>
                <strong>Accuracy</strong> — Get partial credit for being close
              </FactorItem>
              <FactorItem theme={currentTheme}>
                <strong>Insight</strong> — Bonus for reading market direction
              </FactorItem>
              <FactorItem theme={currentTheme}>
                <strong>Courage</strong> — Extra credit for bold, long-term calls
              </FactorItem>
            </FactorList>
            
            <MethodTitle theme={currentTheme}>
              🔗 Learn More
            </MethodTitle>
            <MethodText theme={currentTheme}>
              The Wilson score interval was developed by Edwin Wilson in 1927 
              and is widely used for ranking systems where sample size varies.
            </MethodText>
          </MethodologySection>
        </TwoColumn>
      </ContentWrapper>
    </PageContainer>
  );
};

export default LeaderboardPage;
